import React from 'react';
import { ComponentSchema, ReportCardComponentProps, ReportCardLevel } from './schema-types';
import { componentRegistry } from './components/component-registry';
import { ReportCardLevelService } from '../../../lib/services/report-card-level.service';
import { PositionVisibilityService } from '../../../lib/services/position-visibility.service';

interface SchemaRendererProps {
  schema: ComponentSchema;
  data?: any;
  baseData?: any;
  player: IPlayer;
  playerAssessment: IPlayerAssessment;
  reportCardLevel?: ReportCardLevel;
  organizationLogo?: string | React.ReactNode;
  organizationLogoHeight?: string | number;
  hidePlainTextScores?: boolean;
  context?: { [key: string]: any };
}

export const SchemaRenderer: React.FC<SchemaRendererProps> = ({
  schema,
  baseData: originalBaseData,
  data,
  player,
  playerAssessment,
  reportCardLevel,
  organizationLogo,
  organizationLogoHeight,
  hidePlainTextScores,
  context = {}
}) => {
  // Get player position ID for visibility checks
  const playerPositionId = PositionVisibilityService.getInstance().getPositionId(playerAssessment);

  // Check if component should be shown based on report card level
  if (reportCardLevel && schema.supportedReportCardLevels) {
    const shouldShow = ReportCardLevelService.shouldShowComponent(
      schema.supportedReportCardLevels,
      reportCardLevel
    );

    if (!shouldShow) {
      return null;
    }
  }

  // Check if component should be hidden based on report card level
  if (reportCardLevel && schema.hiddenForReportCardLevels) {
    const shouldHide = schema.hiddenForReportCardLevels.includes(reportCardLevel);

    if (shouldHide) {
      return null;
    }
  }

  // Check if component should be shown based on supported positions
  if (schema.supportedPositions) {
    const shouldShow = PositionVisibilityService.shouldShowComponentForSupportedPositions(
      schema.supportedPositions,
      playerPositionId
    );

    if (!shouldShow) {
      return null;
    }
  }

  // Check if component should be hidden based on hidden positions
  if (schema.hiddenForPositions) {
    const shouldHide = PositionVisibilityService.shouldHideComponentForPositions(
      schema.hiddenForPositions,
      playerPositionId
    );

    if (shouldHide) {
      return null;
    }
  }

  // Get the component from registry
  const Component = componentRegistry[schema.type];

  if (!Component) {
    console.warn(`Component type "${schema.type}" not found in registry`);
    return null;
  }

  // Handle data lookup
  let componentData: any;
  let baseData: any = originalBaseData;
  let schemaData: any;

  // Find schema data if exists in data object
  if (schema.dataKey) {
    schemaData = data?.[schema.dataKey] ?? baseData?.[schema.dataKey]
  }

  if (schema.staticData !== undefined) {
    // Use static data if provided
    componentData = schema.staticData;
    if (typeof componentData === 'object' && schemaData) componentData = {
      ...componentData,
      ...schemaData
    };

    baseData = originalBaseData ?? data;
  } else if (schema.dataKey) {
    const dataIsString = schemaData && typeof schemaData === 'string';
    componentData = dataIsString ? schemaData : {
      ...baseData?.[schema.dataKey],
      ...data?.[schema.dataKey]
    };

    // Check if data exists and component is required
    if (schema.required && (componentData === undefined || componentData === null)) {
      // If required data is missing, try fallback component or skip rendering
      if (schema.fallbackComponent) {
        return (
          <SchemaRenderer
            schema={schema.fallbackComponent}
            data={data}
            baseData={baseData}
            player={player}
            playerAssessment={playerAssessment}
            reportCardLevel={reportCardLevel}
            organizationLogo={organizationLogo}
            organizationLogoHeight={organizationLogoHeight}
            context={context}
          />
        );
      } else {
        console.warn(`Required data key "${schema.dataKey}" not found for component type "${schema.type}"`);
        return null;
      }
    }

    // If not required and data is missing, skip rendering
    if (!schema.required && (componentData === undefined || componentData === null)) {
      return null;
    }
  } else {
    // No data key specified - component will use player/playerAssessment data directly
    componentData = undefined;
  }

  // Ensure position ID is available in base data
  if (baseData && typeof baseData === 'object') {
    baseData = {
      ...baseData,
      positionId: playerPositionId
    };
  } else if (!baseData) {
    baseData = {
      positionId: playerPositionId
    };
  }

  // Render children if they exist
  const children = schema.children?.map((childSchema, index) => (
    <SchemaRenderer
      key={index}
      schema={childSchema}
      baseData={baseData}
      data={data}
      player={player}
      playerAssessment={playerAssessment}
      reportCardLevel={reportCardLevel}
      organizationLogo={organizationLogo}
      organizationLogoHeight={organizationLogoHeight}
      context={context}
    />
  ));

  // Combine props from schema with component data
  // Give priority to schema props for logo configuration when not provided at renderer level
  const componentProps: ReportCardComponentProps = {
    ...schema.props,
    data: componentData,
    baseData,
    player,
    playerAssessment,
    reportCardLevel,
    organizationLogo,
    organizationLogoHeight: organizationLogoHeight || schema.props?.organizationLogoHeight,
    hidePlainTextScores: hidePlainTextScores !== undefined ? hidePlainTextScores : schema.props?.hidePlainTextScores,
    maxWidthRestrictions: schema.maxWidthRestrictions,
    context, // Pass responsive context to all components
    children
  };

  return <Component {...componentProps} />;
};
